import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  compact,
  filter,
  groupBy,
  isEmpty,
  map,
  orderBy,
  toLower,
} from 'lodash';

import { Client } from '@lockton-apps/shared/interfaces';
import { ClientRouterSelectors } from '@lockton-apps/shared/data-access/router';
import { ClientHelpers } from '@lockton-apps/shared/util/helpers';

import {
  clientsAdapter,
  ClientsState,
  SHARED_CLIENTS_FEATURE_KEY,
} from './clients.reducer';

// Lookup the 'Clients' feature state managed by NgRx
export const selectClientsState = createFeatureSelector<ClientsState>(
  SHARED_CLIENTS_FEATURE_KEY
);

const { selectAll, selectEntities } = clientsAdapter.getSelectors();

export const selectClientsLoaded = createSelector(
  selectClientsState,
  (state: ClientsState) => state.loaded
);

export const selectUpdating = createSelector(
  selectClientsState,
  (state: ClientsState) => state.mutationsLoading
);

export const selectClientsError = createSelector(
  selectClientsState,
  (state: ClientsState) => state.error
);

export const selectAllClients = createSelector(
  selectClientsState,
  (state: ClientsState) => selectAll(state)
);

export const selectClientsEntities = createSelector(
  selectClientsState,
  (state: ClientsState) => selectEntities(state)
);

export const selectSelectedId = createSelector(
  selectClientsState,
  (state: ClientsState) => state.selectedId
);

export const selectSelectionIds = createSelector(
  selectClientsState,
  (state: ClientsState) => state.selectionIds
);

export const selectSelection = createSelector(
  selectClientsEntities,
  selectSelectionIds,
  (entities, selectionIds) =>
    filter(
      map(selectionIds, (id) => entities[id]),
      (c) => !isEmpty(c)
    ) as Client[]
);

export const selectAllClientsAndLoaded = createSelector(
  selectAllClients,
  selectClientsLoaded,
  (clients, clientsLoaded) => ({
    clients: orderBy(clients, (c) => toLower(c.clientData.insuredName)),
    clientsLoaded,
  })
);

export const selectEntity = createSelector(
  selectClientsEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined)
);

export const selectRoutedClient = createSelector(
  selectClientsEntities,
  ClientRouterSelectors.selectRoutedId,
  (entities, routedId) => (routedId ? entities[routedId] : undefined)
);

export const selectRoutedClientAndSubClients = createSelector(
  selectRoutedClient,
  selectAllClients,
  (client, allClients) =>
    client ? ClientHelpers.filterClientAndSubClients(allClients, client.id) : []
);

export const selectQueriedClient = createSelector(
  selectClientsEntities,
  ClientRouterSelectors.selectQueriedId,
  (entities, queriedId) => (queriedId ? entities[queriedId] : undefined)
);

export const selectQueriedClientAndSubClients = createSelector(
  selectQueriedClient,
  selectAllClients,
  (client, allClients) =>
    client ? ClientHelpers.filterClientAndSubClients(allClients, client.id) : []
);

export const getContactAddComplete = createSelector(
  selectClientsState,
  (state: ClientsState) => state.contactAddComplete
);

export const getSubClientsAddComplete = createSelector(
  selectClientsState,
  (state: ClientsState) => state.subClientsAddComplete
);

export const getAddedClientId = createSelector(
  selectClientsState,
  (state: ClientsState) => state.addedClientId
);

export const getSelectedAndLoaded = createSelector(
  selectEntity,
  selectClientsLoaded,
  (selectedClient, clientsLoaded) => ({ selectedClient, clientsLoaded })
);

export const getAddedClient = createSelector(
  selectClientsEntities,
  getAddedClientId,
  (entities, addedId) => (addedId ? entities[addedId] : undefined)
);

export const getSubsidiariesMap = createSelector(
  selectAllClients,
  (clients) => {
    const subsidiariesMap = groupBy(
      filter(clients, (x) => !x.isPrimaryClient),
      (x) => x.relatesToClientId
    );
    return subsidiariesMap;
  }
);

// export const getRelatedIds = createSelector(
//   selectClientsState,
//   (state: ClientsState) => state.relatedIds
// );

export const selectRoutedClientRelatedClients = createSelector(
  selectAllClients,
  selectRoutedClient,
  (entities, routedClient) =>
    routedClient
      ? ClientHelpers.filterForRelatedClients(
          entities,
          compact([routedClient.id, routedClient.relatesToClientId])
        )
      : []
);

// export const getRelatedOmitSelected = createSelector(
//   getRelatedClients,
//   selectSelectedId,
//   (relatedClients, selectedId) =>
//     filter(relatedClients, (c) => c.id !== selectedId)
// );

// export const getSelectedAndRelated = createSelector(
//   getRelatedClients,
//   selectEntity,
//   (relatedClients, selectedClient) => ({ relatedClients, selectedClient })
// );

export const getCreateUpdateStepperState = createSelector(
  selectClientsState,
  (state: ClientsState) => state.createUpdate
);

export const selectSortedSelection = createSelector(
  selectSelection,
  (clients) => orderBy(clients, (c) => toLower(c.clientData.insuredName))
);

export const selectRoutedClientCobId = createSelector(
  selectRoutedClient,
  (client) => client?.classOfBusinessId
);

export const selectQueriedClientCobId = createSelector(
  selectQueriedClient,
  (client) => client?.classOfBusinessId
);
